import React, { useState } from 'react'
import {
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js'
import { Radio, Notification, toast } from 'components/ui'
import { useDispatch, useSelector } from 'react-redux'
import { apiVerifyTransaction } from 'services/BillingService'
import { clearCheckout } from 'store/checkout/titlesSlice'
import { clearFiltersList } from 'store/search/filtersSlice'
import FormFooter from '../FormFooter'
import { setSubscription } from 'store/base/commonSlice'
import {
    setChangePackageTo,
    setPlanPanelExpand,
} from 'store/checkout/selectPlanSlice'
import {
    FaCalendarAlt,
    FaCreditCard,
    FaLock,
    FaCcVisa,
    FaCcMastercard,
    FaCcAmex,
} from 'react-icons/fa'

const elementStyles = {
    style: {
        base: {
            color: '#32325d',
            fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': {
                color: '#aab7c4',
            },
            padding: '10px', // Padding to create space for icons
            borderRadius: '4px',
            backgroundColor: '#ffffff',
        },
        invalid: {
            color: '#fa755a',
            iconColor: '#fa755a',
        },
        complete: {
            iconColor: '#4caf50',
        },
    },
}

const CreditCardCheckoutForm = ({
    subTotalAmount,
    totalAmount,
    startingDate,
    selectedPackage,
    paymentMethods,
    termsOfUseContent,
}) => {
    const { selectedTitles, premiumTitlesCount, standardTitlesCount } =
        useSelector((state) => state.checkout.titles)
    const dispatch = useDispatch()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
        paymentMethods.length === 0 ? 'new' : paymentMethods[0]['id']
    )

    const stripe = useStripe()
    const elements = useElements()
    const { changePackageTo } = useSelector(
        (state) => state.checkout.selectPlan
    )

    // Function to handle payment method change
    const handlePaymentMethodChange = (val) => {
        setSelectedPaymentMethod(val)
    }

    // Function to render card brand logos
    const renderCardBrandLogo = (brand) => {
        switch (brand.toLowerCase()) {
            case 'visa':
                return <FaCcVisa className="text-blue-500" />
            case 'mastercard':
                return <FaCcMastercard className="text-red-500" />
            case 'amex':
                return <FaCcAmex className="text-green-500" />
            default:
                return null
        }
    }

    const handleSubmit = async (event) => {
        event.preventDefault()

        if (!stripe || !elements) {
            // Stripe.js hasn't yet loaded
            return
        }

        setIsSubmitting(true)
        let paymentMethodId

        if (selectedPaymentMethod === 'new') {
            // Create a payment method using Stripe Elements
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: elements.getElement(CardNumberElement),
            })

            if (error) {
                toast.push(
                    <Notification
                        title="Unable To Proceed"
                        type="danger"
                        closable
                        duration={10000}
                    >
                        <div className="text-red-400 font-medium">
                            {error.message}
                        </div>
                    </Notification>
                )
                setIsSubmitting(false)
                return
            }

            paymentMethodId = paymentMethod.id
        } else {
            // Use the selected saved payment method
            paymentMethodId = selectedPaymentMethod
        }

        // Send the payment method to the backend to create a subscription/payment
        const response = await apiVerifyTransaction({
            paymentMethodId, // Send selected or newly created payment method ID
            selectedPackage: selectedPackage.id,
            selectedTitles: selectedTitles.map((title) => title.id),
            premiumTitlesCount,
            standardTitlesCount,
            upgrade: changePackageTo?.id ? true : false,
        })

        const result = response.data
        console.log(result)

        if (result.error) {
            toast.push(
                <Notification
                    title="Sorry, Something Went Wrong"
                    type="danger"
                    closable
                    duration={10000}
                >
                    <div className="text-red-400 font-medium">
                        {result.error}
                    </div>
                </Notification>
            )
        } else if (result.payment_intent_status === 'requires_action') {
            // Handle 3D Secure (SCA) authentication
            const { error: confirmError } = await stripe.confirmCardPayment(
                result.client_secret
            )

            if (confirmError) {
                toast.push(
                    <Notification
                        title="3D Secure Authentication Failed"
                        type="danger"
                        closable
                        duration={10000}
                    >
                        <div className="text-red-400 font-medium">
                            {confirmError.message}
                        </div>
                    </Notification>
                )
            } else {
                await dispatch(clearCheckout())
                await dispatch(setPlanPanelExpand(false))
                await dispatch(clearFiltersList())
                await dispatch(setSubscription(result.subscription))
                await dispatch(setChangePackageTo(null))

                toast.push(
                    <Notification
                        title="Payment Completed Successfully"
                        type="success"
                        closable
                        duration={10000}
                    >
                        <div>
                            Thank you for your payment. Your transaction has
                            been completed successfully.
                        </div>
                        <div>
                            If you have any questions or need further
                            assistance, please don't hesitate to contact us.
                        </div>
                    </Notification>
                )
            }
        } else if (result.status === 'success') {
            await dispatch(setSubscription(result.subscription))
            await dispatch(clearCheckout())
            await dispatch(setPlanPanelExpand(false))
            await dispatch(clearFiltersList())
            await dispatch(setChangePackageTo(null))

            toast.push(
                <Notification
                    title="Payment Completed Successfully"
                    type="success"
                    closable
                    duration={10000}
                >
                    <div>
                        Thank you for your payment. Your transaction has been
                        completed successfully.
                    </div>
                    <div>
                        If you have any questions or need further assistance,
                        please don't hesitate to contact us.
                    </div>
                </Notification>
            )
        }

        setIsSubmitting(false)
    }

    return (
        <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-10 pb-10">
                {paymentMethods.length > 0 ? (
                    <div className="mb-1">
                        <h6>Select a Payment Method</h6>
                        <div className="mt-4">
                            <Radio.Group
                                vertical
                                value={selectedPaymentMethod}
                                onChange={handlePaymentMethodChange}
                            >
                                {/* Iterate over saved payment methods */}
                                {paymentMethods.map((method) => (
                                    <Radio
                                        key={method.id}
                                        value={method.id}
                                        className={
                                            selectedPaymentMethod === method.id
                                                ? 'text-red-500 font-medium'
                                                : 'text-zinc-500'
                                        }
                                    >
                                        <div className="flex items-center">
                                            {/* Render card brand logo */}
                                            {renderCardBrandLogo(method.brand)}
                                            <span className="ml-2">
                                                {method.brand.toUpperCase()}{' '}
                                                ending in {method.last4} ( exp{' '}
                                                {method.exp_month}/
                                                {method.exp_year})
                                            </span>
                                        </div>
                                    </Radio>
                                ))}
                                {/* Allow user to select 'Credit Card' for new card entry */}
                                <Radio
                                    value={'new'}
                                    className={
                                        selectedPaymentMethod === 'new'
                                            ? 'text-red-500 font-medium'
                                            : 'text-zinc-500'
                                    }
                                >
                                    Add New Credit Card
                                </Radio>
                            </Radio.Group>
                        </div>
                    </div>
                ) : (
                    <></>
                )}
                {selectedPaymentMethod === 'new' && (
                    <div className="mb-3 ">
                        <h5>Add New Card Details</h5>
                        <div className="flex flex-col justify-between items-center gap-2 mt-3 border p-4 rounded">
                            <div className="w-full flex justify-between items-center gap-2">
                                <div className="flex flex-col basis-1/2">
                                    <p className="pb-2 uppercase font-semibold text-xs">
                                        Card Number
                                    </p>
                                    <div className="w-full flex items-center border border-gray-300 p-3 rounded">
                                        <div className="flex-grow">
                                            <CardNumberElement
                                                options={elementStyles}
                                            />
                                        </div>
                                        <FaCreditCard className="ml-2" />
                                    </div>
                                </div>

                                <div className="flex flex-col basis-1/4">
                                    <p className="pb-2 uppercase font-semibold text-xs">
                                        Expiry Date
                                    </p>
                                    <div className="w-full flex items-center border border-gray-300 p-3 rounded">
                                        <div className="flex-grow">
                                            <CardExpiryElement
                                                options={elementStyles}
                                            />
                                        </div>
                                        <FaCalendarAlt className="ml-2" />
                                    </div>
                                </div>

                                <div className="flex flex-col basis-1/4">
                                    <p className="pb-2 uppercase font-semibold text-xs">
                                        CVV
                                    </p>
                                    <div className="w-full flex items-center border border-gray-300 p-3 rounded">
                                        <div className="flex-grow">
                                            <CardCvcElement
                                                options={elementStyles}
                                            />
                                        </div>
                                        <FaLock className="ml-2" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <FormFooter
                    subTotalAmount={subTotalAmount}
                  
                    totalAmount={totalAmount}
                    isSubmitting={isSubmitting}
                    startingDate={startingDate}
                    termsOfUseContent={termsOfUseContent}
                />
            </div>
        </form>
    )
}

export default CreditCardCheckoutForm
